import React, {
	Suspense,
	useState,
	useCallback,
	useEffect,
	useMemo,
} from "react"

import { ToastContainer, toast } from "react-toastify"

// import base64 from "base-64"
// import iconv from "iconv-lite"
// import queryString from "query-string"
// import { throttle } from "lodash"

import AboutModal from "./components/AboutModal"
import Composition from "./components/Composition"
import { WebGLComposition, TextureLoader } from "./components/WebGLComposition"

import News from "./components/News"
import { Sounds } from "./Sounds"

import images from "./content/round_7_images"

import { SPEED, PAUSE_DURATION } from "./config"

import { useInterval } from "./hooks/useInterval"

import { ReactComponent as InfoSVG } from "./assets/help.svg"
import { ReactComponent as ShareSVG } from "./assets/share.svg"
import { ReactComponent as VolumeMuteSVG } from "./assets/volume_off.svg"
import { ReactComponent as VolumeUpSVG } from "./assets/volume_up.svg"
import { ReactComponent as PlaySVG } from "./assets/play.svg"

// import stories from "./content/stories.json"

import "./App.sass"
import "./toastify.css"

import { Splash } from "./Splash"

function Loader({ isVisible }) {
	return <div className="LoadingText">{isVisible && "Loading ..."}</div>
}

export function VolumeToggle({ isActive, onClick }) {
	return (
		<div className="VolumeButton" onClick={onClick}>
			{!isActive ? <VolumeMuteSVG /> : <VolumeUpSVG />}
		</div>
	)
}

// function format({ title = "", description = "" }) {
// 	const formattedTitle = title.includes(" | ")
// 		? title.substring(0, title.lastIndexOf(" | "))
// 		: title.includes(" - ")
// 		? title.substring(0, title.lastIndexOf(" - "))
// 		: title
// 	return `${formattedTitle}\n${description}`
// }

function randomArticle(stories) {
	return stories[Math.floor(Math.random() * stories.length)]
}

export default function App({ stories }) {
	const [isSoundActive, setSoundActive] = useState(false)
	const [isSoundAllowed, setSoundAllowed] = useState(true)
	const [currentIndex, setCurrentIndex] = useState("")
	const [article, setArticle] = useState(randomArticle(stories))
	const [areTexturesLoaded, setTexturesLoaded] = useState(false)
	const [areSoundsLoaded, setSoundsLoaded] = useState(false)
	const [isAnimating, setAnimating] = useState(false)
	const [isModalOpen, setModalOpen] = useState(false)
	const [isSplashDismissed, setSplashDismissed] = useState(false)

	const isReady = useMemo(
		() => (areTexturesLoaded && areSoundsLoaded) || isAnimating,
		[areSoundsLoaded, areTexturesLoaded, isAnimating],
	)

	const handleShareClick = useCallback(async (e) => {
		await navigator.clipboard.writeText(window.location.href)

		toast.info("Copied URL to clipboard", {
			hideProgressBar: true,
			position: toast.POSITION.TOP_RIGHT,
		})
	}, [])

	const handleSoundClick = useCallback(() => {
		if (isSoundActive) {
			setSoundActive(null)
		} else {
			setSoundActive(true)
		}
	}, [isSoundActive])

	const handleTextureLoad = useCallback(() => {
		setTexturesLoaded(true)
	}, [])

	const handleAudioLoad = useCallback(() => {
		setSoundsLoaded(true)
	}, [])

	const handleSplashDismissal = useCallback(
		(e) => {
			setSplashDismissed(true)
			isSoundAllowed && setSoundActive(true)
		},
		[isSoundAllowed],
	)
	const handlePlayClick = useCallback((e) => {
		setCurrentIndex(0)
		setAnimating(true)
	}, [])

	useEffect(() => {
		if (isSplashDismissed) {
			if (areTexturesLoaded) {
				if (isSoundAllowed) {
					if (areSoundsLoaded) {
						setAnimating(true)
					}
				} else {
					setAnimating(true)
				}
			}
		}
	}, [areTexturesLoaded, areSoundsLoaded, isSoundAllowed, isSplashDismissed])

	useInterval(() => {
		if (
			article.description &&
			isAnimating &&
			currentIndex < article.description.length + PAUSE_DURATION
		) {
			const adder = article.description[currentIndex + 1] === " " ? 2 : 1
			const newIndex = currentIndex + adder
			setCurrentIndex(newIndex)
		} else if (isSplashDismissed) {
			setCurrentIndex(0)
			let newArticle = randomArticle(stories)
			while (newArticle === article) {
				newArticle = randomArticle(stories)
			}
			setArticle(newArticle)
			setAnimating(true)
		}
	}, SPEED)

	const truncatedDescription =
		article && typeof article.description === "string"
			? article.description.substring(0, currentIndex)
			: ""

	return (
		<div className="App">
			<AboutModal isOpen={isModalOpen} setOpen={setModalOpen} />
			<ToastContainer />

			{!isSplashDismissed && (
				<Splash
					isSoundAllowed={isSoundAllowed}
					setSoundAllowed={setSoundAllowed}
					onConfirm={handleSplashDismissal}
				/>
			)}

			<div className="layout">
				{isReady && isSplashDismissed && (
					<News truncated={truncatedDescription} article={article} />
				)}

				<Composition seed={truncatedDescription} images={images}>
					{(elements) => (
						<>
							{elements ? (
								<>
									<Sounds
										isActive={isSoundActive}
										onLoad={handleAudioLoad}
										seed={elements.map((e) => e.word).join(" ")}
									/>
									<Suspense fallback={<Loader isVisible={isSplashDismissed} />}>
										<TextureLoader
											elements={elements}
											onLoad={handleTextureLoad}
										>
											{({ textures }) => (
												<WebGLComposition
													elements={elements}
													textures={textures}
												/>
											)}
										</TextureLoader>
									</Suspense>
								</>
							) : null}
						</>
					)}
				</Composition>
			</div>

			{isSplashDismissed && isReady && (
				<div className={`actions`}>
					<InfoSVG
						className="InfoButton"
						onClick={(e) => setModalOpen(true)}
						title="More info"
					/>

					<VolumeToggle
						onClick={handleSoundClick}
						isActive={isSoundActive}
						title="Toggle audio"
					/>

					{false && (
						<>
							<div className="stretch" />

							{!isAnimating && (
								<div
									className="PlayButton"
									onClick={handlePlayClick}
									title="Replay article"
								>
									<PlaySVG />
								</div>
							)}

							<div
								className="ShareButton"
								onClick={handleShareClick}
								title="Share article"
							>
								<ShareSVG />
							</div>
						</>
					)}
				</div>
			)}
		</div>
	)
}
